import { Injectable, PLATFORM_ID, inject, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

import { environment } from '@environments/environment';

/** Tracks browser network connectivity (online/offline) for UI indicators. */
@Injectable({ providedIn: 'root' })
export class NetworkStatusService {
  private readonly _platformId = inject(PLATFORM_ID);

  private readonly _isOnline = signal(
    isPlatformBrowser(this._platformId) ? navigator.onLine : true
  );

  /** True when the browser reports a network connection. */
  public readonly isOnline = this._isOnline.asReadonly();

  constructor() {
    if (isPlatformBrowser(this._platformId)) {
      window.addEventListener('online', this._setOnline);
      window.addEventListener('offline', this._setOffline);
      this._startPeriodicCheck();
    }
  }

  private _startPeriodicCheck(): void {
    const intervalMinutes = environment.networkStatusCheckIntervalMinutes ?? 2;
    const intervalMs = intervalMinutes * 60 * 1000;
    setInterval(() => {
      this._isOnline.set(navigator.onLine);
    }, intervalMs);
  }

  private readonly _setOnline = (): void => this._isOnline.set(true);
  private readonly _setOffline = (): void => this._isOnline.set(false);
}
